/*
 * Authors: mariushirjanu, Koen, Triadi
 * Created: 30/10/2015
 * Upgraded: 04/04/2023
 */
import { GaWizardService } from '@app/shared/wizard/ga-wizard.service';
import { GaWizard } from '@app/shared/wizard/ga-wizard';
import { WIZARD_ORDER } from '@app/shared/app-wizards';
import { OrderEntryShippingService } from './shipping/order-entry-shipping.service';
import { OrderEntryInvoiceService } from './invoice/order-entry-invoice.service';
import { IInvoiceAddress, InvoiceAddressType, IOrderEntryContact, IShippingAddress, LocationDataType, LocationProperty, OrderEntryLocationDataService, ShippingAddressType } from './order-entry-location-data.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { IContact, ILocationExtended } from '@type_definitions/datatypes';
import { OrderEntryRequesterService } from '@app/main/order/entry/requester/order-entry-requester.service';
import { OrderEntryWebshopService } from '@app/main/order/entry/webshop/order-entry-webshop.service';
import { OrderEntryService } from '@app/main/order/entry/order-entry.service';
import { util } from '@app/shared/util';
import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { OrderEntryStepId } from '@app/main/order/entry/order-entry-steps';
import { BillingType } from '@app/main/order/entry/order-entry-data.service';
import { IValidatingComponent } from '@app/shared/formly/ga-validate.service';

@Injectable()
export abstract class BaseOrderEntryLocationComponent implements IValidatingComponent, OnInit, OnDestroy {
  public locationToModel:OrderEntryShippingService|OrderEntryInvoiceService;
  public otherLocationToModel:OrderEntryShippingService|OrderEntryInvoiceService;
  public location:ILocationExtended;
  public selectedAddress:IShippingAddress | IInvoiceAddress;
  public addressType:InvoiceAddressType | ShippingAddressType;
  public dataType:LocationDataType;
  public loading:boolean;
  public billingType:BillingType;
  public fixedList:boolean;

  protected wizard:GaWizard;
  protected onAddressInitDeferred:IGADeferred<void>;
  protected onContactInitDeferred:IGADeferred<void>;

  public abstract validate():Promise<void | string[]>;
  public abstract beforeValidate():Promise<boolean | void>;

  protected constructor(
    protected orderEntryRequesterService:OrderEntryRequesterService,
    protected orderEntryWebshopService:OrderEntryWebshopService,
    protected orderEntryLocationDataService:OrderEntryLocationDataService,
    protected orderEntryService:OrderEntryService,
    protected wizardService:GaWizardService,
    deferredService:GaDeferredService
  ) {
    this.wizard = this.wizardService.getInstance(WIZARD_ORDER);
    this.onContactInitDeferred = deferredService.defer();
    this.onAddressInitDeferred = deferredService.defer();
  }
  
  public ngOnInit():void {
    this.billingType = this.orderEntryService.billingTypeId;
    this.fixedList = this.isInvoiceDataType ? this.orderEntryWebshopService.shopContext?.invoiceFixedListSelection : this.orderEntryWebshopService.shopContext?.shippingFixedListSelection;
    
    const wizardStepId:OrderEntryStepId = this.isInvoiceDataType ? OrderEntryStepId.INVOICETO : OrderEntryStepId.SHIPTO;
    this.wizard.currentValidator(this.validate, true, this.beforeValidate, wizardStepId);
    
    // we need to load this before rendering the screen
    this.loadContactAddressOptions();

    // init wizard setup
    this.initWizardSetup();
  }
  
  private get isInvoiceDataType():boolean {
    return this.dataType === LocationDataType.INVOICE;
  }

  private initWizardSetup():void {
    this.wizard = this.wizardService.getInstance(WIZARD_ORDER);
  }

  public onLoadAddressDone():void {
    this.onAddressInitDeferred.resolve();
  }

  public onLoadContactDone():void {
    this.onContactInitDeferred.resolve();
  }

  public onAddressTypeChange = (addressType:InvoiceAddressType | ShippingAddressType):void => {
    this.addressType = addressType;
  };

  public onLocationChange = (newLocation:ILocationExtended):void => {
    this.location = newLocation;
  };
  
  protected loadContactAddressOptions():void {
    const requesterId:number = this.orderEntryRequesterService.contact?.id;
    const selectedShopId:number = this.orderEntryWebshopService.selectedShop?.id;
    const selectedCountryId:number = this.orderEntryWebshopService.selectedCountry?.id;
    const testMode:boolean = this.orderEntryWebshopService.testMode;
    const bpId:number = this.orderEntryWebshopService.selectedBP?.id;
    const locationProp:LocationProperty = this.orderEntryLocationDataService.locationProperty(this.dataType);
    const loadedContact:IContact = this.orderEntryService.order?.[ locationProp ]?.contact;

    if (selectedShopId && selectedCountryId) {
      this.loading = true;
      Promise.all([
        this.orderEntryLocationDataService.contacts(this.dataType, requesterId, selectedShopId, selectedCountryId, this.billingType),
        this.orderEntryLocationDataService.addresses(this.dataType, requesterId, selectedShopId, selectedCountryId, testMode, bpId, this.billingType)
      ]).then(util.spread((contactData:IOrderEntryContact[], addressData:IInvoiceAddress[]|IShippingAddress[]) => {
        // !!! setContactTypeOptions also updates contacts data to model (if any)
        this.locationToModel.setContactTypeOptions(contactData, !!loadedContact, this.orderEntryService.requesterRequired);
        // !!! setAddressTypeOptions also updates address data to model
        this.locationToModel.setAddressTypeOptions(addressData, true);
      })).finally(() => {
        this.loading = false;
      });
    }
  }

  public ngOnDestroy():void {
    this.wizard.clearCurrentValidator();
  }
}